import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Textarea } from '@/shared/components/ui/textarea';
import { FilePicker } from '@/shared/components/file-upload/FilePicker';
import { uploadDocument } from '@/shared/api/documents';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { MoneyInput } from '@/features/ventas/components/MoneyInput';
import {
  listTesoreriaCategorias,
  type CategoriasSubCategoriasItem,
} from '@/shared/api/controllers/categoriasSubCategorias';
import { crearMovimiento, getCajaSaldo } from '../api';

/**
 * Manual cash-movement entry dialog. Shared by the Carga de Ingreso +
 * Carga de Egreso flows. Mirrors PowerApps PopUp_AddIngreso /
 * PopUp_AddEgreso (Screen_Tesoreria.pa.yaml:1154 / 1409) — three
 * cascading dropdowns from `99.ABM_CategoriasSubCategorias`
 * (Categoria → Cuenta contable → Descripción) + Monto + Observación.
 *
 * Egreso shows a "Disponible: $X" hint next to the Monto label sourced
 * from `getCajaSaldo` (cross-month Pendiente saldo) — same source the
 * server uses to enforce the negative-saldo guard, so the hint and the
 * server's rejection stay in sync.
 *
 * Auto-tax rows (Ley 25.413, SIRCREB, Morvalab) are posted server-side
 * when the caja + tipo combination matches — see
 * [routes/tesoreria.ts POST /movimiento](../../../server/src/routes/tesoreria.ts).
 *
 * Los cargos de MercadoPago (botón amarillo) van por su propio diálogo:
 * `CargosMercadoPagoDialog`.
 */
export function AgregarMovimientoDialog({
  tipo,
  caja,
  moneda,
  onClose,
}: {
  tipo: 'Ingreso' | 'Egreso';
  /** The caja selected in the page toolbar. Locked — users change caja via the page, not the dialog. */
  caja: string;
  moneda: 'ARS' | 'MXN' | 'USD';
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [categoria, setCategoria] = useState<string>('');
  const [cuentaContable, setCuentaContable] = useState<string>('');
  const [descripcion, setDescripcion] = useState<string>('');
  const [monto, setMonto] = useState<string>('');
  // Sólo se pide en cajas en dólares. Sin esto la fila queda sin
  // equivalente en pesos y el Estado Financiero no la puede convertir.
  const [tipoCambio, setTipoCambio] = useState<string>('');
  const [observaciones, setObservaciones] = useState<string>('');
  // Factura / comprobante opcional (pedido 2026-10): si hay archivo se sube
  // después de crear el movimiento; si no, no se hace nada.
  const [comprobante, setComprobante] = useState<File | null>(null);

  const catalogQ = useQuery({
    queryKey: ['tesoreria', 'categorias-subcategorias'],
    queryFn: listTesoreriaCategorias,
    staleTime: 5 * 60_000,
  });

  // Saldo for the "Disponible" hint on Egresos. Skipped on Ingresos —
  // PowerApps PopUp_AddIngreso doesn't show one either.
  //
  // `gcTime: 0` drops the cache as soon as this dialog closes so the
  // next open cannot show a stale value while it refetches. Without
  // this the dialog briefly displayed `Disponible: -$X` cached from a
  // prior race during a concurrent Cierre de Caja (bug 2026-05-08).
  // `refetchOnMount: 'always'` is belt-and-suspenders — even if the
  // cache survives, force a fresh fetch every time the dialog opens.
  const saldoQ = useQuery({
    queryKey: ['tesoreria', 'saldo', caja],
    queryFn: () => getCajaSaldo(caja),
    enabled: tipo === 'Egreso',
    gcTime: 0,
    refetchOnMount: 'always',
  });
  const saldoDisponible = saldoQ.data?.saldo ?? 0;
  // Hide the value while loading or refetching — showing 0 falsely
  // signals "no funds available" before the real number lands.
  const saldoLoading = tipo === 'Egreso' && (saldoQ.isPending || saldoQ.isFetching);

  const activeRows = useMemo(
    () => (catalogQ.data ?? []).filter((r) => r.fields.Status_CSC !== 'Inactivo'),
    [catalogQ.data],
  );

  const categorias = useMemo(() => {
    const set = new Set<string>();
    for (const r of activeRows) {
      const c = (r.fields.Categoria_CSC ?? '').trim();
      if (c) set.add(c);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [activeRows]);

  const cuentasContables = useMemo(() => {
    if (!categoria) return [] as string[];
    const set = new Set<string>();
    for (const r of activeRows) {
      if ((r.fields.Categoria_CSC ?? '').trim() !== categoria) continue;
      const cc = (r.fields.CuentaContable_CSC ?? '').trim();
      if (cc) set.add(cc);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [activeRows, categoria]);

  const descripciones = useMemo<CategoriasSubCategoriasItem[]>(() => {
    if (!categoria || !cuentaContable) return [];
    return activeRows.filter(
      (r) =>
        (r.fields.Categoria_CSC ?? '').trim() === categoria &&
        (r.fields.CuentaContable_CSC ?? '').trim() === cuentaContable,
    );
  }, [activeRows, categoria, cuentaContable]);

  const montoNum = parseMoney(monto) ?? 0;
  const esCajaUsd = moneda === 'USD';
  const tipoCambioNum = parseMoney(tipoCambio) ?? 0;
  /** Equivalente en pesos que se guarda junto al movimiento. */
  const montoEnPesos = esCajaUsd ? montoNum * tipoCambioNum : 0;

  const validationError = useMemo<string | null>(() => {
    if (!categoria) return 'Seleccioná una categoría.';
    if (!cuentaContable) return 'Seleccioná una cuenta contable.';
    if (!descripcion) return 'Seleccioná una descripción.';
    if (montoNum <= 0) return 'Ingresá un monto mayor a cero.';
    if (esCajaUsd && tipoCambioNum <= 0)
      return 'Ingresá el tipo de cambio: la caja opera en dólares.';
    if (tipo === 'Egreso' && montoNum > saldoDisponible + 0.01)
      return `El monto excede el saldo disponible (${formatMoney(saldoDisponible, moneda)}).`;
    return null;
  }, [
    categoria,
    cuentaContable,
    descripcion,
    montoNum,
    tipo,
    saldoDisponible,
    moneda,
    esCajaUsd,
    tipoCambioNum,
  ]);

  const mutation = useMutation({
    mutationFn: async () => {
      const r = await crearMovimiento({
        tipo,
        caja,
        monto: montoNum,
        categoria,
        cuentaContable,
        descripcion,
        observaciones: observaciones.trim() || undefined,
        ...(esCajaUsd ? { tipoCambio: tipoCambioNum } : {}),
      });
      // El movimiento ya quedó registrado: si el archivo falla no se
      // deshace, se avisa para adjuntarlo desde el detalle.
      let adjuntoError: string | null = null;
      if (r.outcome === 'created' && comprobante) {
        try {
          await uploadDocument({ entityType: 'ComprobanteTesoreria', entityId: r.id, file: comprobante });
        } catch (err) {
          adjuntoError = err instanceof Error ? err.message : 'Error desconocido';
        }
      }
      return { r, adjuntoError };
    },
    onSuccess: ({ r, adjuntoError }) => {
      if (r.outcome === 'created') {
        const leyNote =
          r.impuestoRows > 0 ? ` · Impuestos: ${formatMoney(r.impuestoTotal, 'ARS')}` : '';
        toast.success(`${tipo} registrado${leyNote}`, {
          description: comprobante && !adjuntoError ? `Comprobante adjunto: ${comprobante.name}` : undefined,
        });
        if (adjuntoError) {
          toast.warning('No se pudo adjuntar el comprobante', {
            description: `${adjuntoError}. Podés adjuntarlo desde el detalle del movimiento.`,
          });
        }
        qc.invalidateQueries({ queryKey: ['tesoreria'] });
        onClose();
      } else {
        toast.warning('No se pudo registrar', { description: r.message });
      }
    },
    onError: (err) =>
      toast.error('No se pudo registrar el movimiento', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });

  const canSubmit = validationError === null && !mutation.isPending;

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-2xl"
        processing={
          mutation.isPending &&
          (tipo === 'Ingreso' ? 'Registrando ingreso…' : 'Registrando egreso…')
        }
      >
        <DialogHeader>
          <DialogTitle>{`Carga de ${tipo}`}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="mov-categoria">Categoria</Label>
              <Select
                value={categoria}
                onValueChange={(v) => {
                  setCategoria(v);
                  setCuentaContable('');
                  setDescripcion('');
                }}
                disabled={catalogQ.isPending || categorias.length === 0}
              >
                <SelectTrigger id="mov-categoria" variant="filled">
                  <SelectValue
                    placeholder={catalogQ.isPending ? 'Cargando…' : 'Seleccionar…'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {categorias.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="mov-cuenta">Cuenta contable</Label>
              <Select
                value={cuentaContable}
                onValueChange={(v) => {
                  setCuentaContable(v);
                  setDescripcion('');
                }}
                disabled={!categoria || cuentasContables.length === 0}
              >
                <SelectTrigger id="mov-cuenta" variant="filled">
                  <SelectValue
                    placeholder={!categoria ? 'Elegí Categoría' : 'Seleccionar…'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {cuentasContables.map((cc) => (
                    <SelectItem key={cc} value={cc}>
                      {cc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="mov-descripcion">Descripción</Label>
              <Select
                value={descripcion}
                onValueChange={setDescripcion}
                disabled={!cuentaContable || descripciones.length === 0}
              >
                <SelectTrigger id="mov-descripcion" variant="filled">
                  <SelectValue
                    placeholder={!cuentaContable ? 'Elegí Cuenta' : 'Seleccionar…'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {descripciones.map((r) => (
                    <SelectItem
                      key={r.id}
                      value={(r.fields.Descripcion_CSC ?? '').trim()}
                    >
                      {r.fields.Descripcion_CSC ?? '—'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              {/* Plain `<Label>` (matching the Categoria / Cuenta /
                  Descripción columns) so the input below sits at the
                  same vertical offset across all four fields. The
                  Egreso "Disponible" hint moved beneath the input. */}
              <Label htmlFor="mov-monto">Monto</Label>
              <MoneyInput
                id="mov-monto"
                variant="filled"
                placeholder="Ingresar monto"
                value={monto}
                moneda={moneda}
                onChange={setMonto}
              />
              {tipo === 'Egreso' && (
                <p className="flex items-center gap-1 text-xs font-medium text-rose-700">
                  {saldoLoading ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                      <span>Calculando saldo…</span>
                    </>
                  ) : (
                    <>Disponible: {formatMoney(saldoDisponible, moneda)}</>
                  )}
                </p>
              )}
            </div>

            {/* Cajas en dólares: el equivalente en pesos se guarda con el
                movimiento. Sin esto el Estado Financiero no puede convertirlo
                y el importe queda estimado. */}
            {esCajaUsd && (
              <div className="space-y-1.5">
                <Label htmlFor="mov-tipo-cambio">Tipo de cambio</Label>
                <MoneyInput
                  id="mov-tipo-cambio"
                  variant="filled"
                  placeholder="Pesos por dólar"
                  value={tipoCambio}
                  moneda="ARS"
                  onChange={setTipoCambio}
                />
                <p className="text-xs text-muted-foreground">
                  {montoEnPesos > 0
                    ? `Equivale a ${formatMoney(montoEnPesos, 'ARS')}`
                    : 'La caja opera en dólares: se guarda el equivalente en pesos.'}
                </p>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mov-obs">Observación</Label>
            <Textarea
              id="mov-obs"
              variant="filled"
              placeholder="Ingresar observación"
              rows={4}
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Factura o comprobante</Label>
            <FilePicker file={comprobante} onChange={setComprobante} disabled={mutation.isPending} />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
            title={validationError ?? undefined}
            className="gap-2 bg-foreground text-background hover:bg-foreground/90"
          >
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
